import { useState } from 'react';
import { useAction } from '../../engine/api/hooks';
import { Button, IconButton } from '../../engine/design/primitives/Button';
import { Badge } from '../../engine/design/primitives/Misc';
import { formatSeconds, useTicketTimer } from './lib/timeCapture';

// The ticket's timer (docs/servicedesk-v2-proposal.md P6). It counts while the technician is
// actually here, and turns into time with one click — the server rounds under the tenant's policy,
// so what is logged is what will be billed. Nothing is billed without this click.

export function TimerControl({ ticketId, autoStart, onLogged }: { ticketId: string; autoStart?: boolean; onLogged?: () => void }) {
  const timer = useTicketTimer(ticketId, { autoStart });
  const log = useAction<{ minutes: number; time_entry_id: string }>('time-captures', 'log', { toast: { success: (r) => `Logged ${(r as { minutes: number }).minutes} minutes` } });
  const [logging, setLogging] = useState(false);

  // A Halo-connected organisation logs time on the ticket's conversation; no local timer.
  if (timer.unavailable) return null;

  if (!timer.running) {
    return (
      <Button size="sm" variant="ghost" icon="timer" busy={timer.busy} onClick={timer.start} title="Start capturing time on this ticket">
        Start timer
      </Button>
    );
  }

  return (
    <span className="hf-row" style={{ gap: 'var(--s-1)' }}>
      <Badge tone="accent" title="Time captured on this ticket in this session">
        <span className="hf-num">{formatSeconds(timer.seconds)}</span>
      </Badge>
      <IconButton icon="pause" label="Pause the timer" size="sm" onClick={timer.stop} />
      <Button
        size="sm"
        variant="outline"
        icon="check"
        busy={log.isPending || logging}
        onClick={() => {
          if (!timer.session) return;
          setLogging(true);
          log.mutate(
            { id: timer.session.id, body: {} },
            {
              onSettled: () => setLogging(false),
              onSuccess: () => onLogged?.(),
            },
          );
        }}
        title="Turn the captured time into a time entry"
      >
        Log time
      </Button>
    </span>
  );
}
